import { useState, useCallback, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { AuthProvider, useAuth } from "@/AuthContext";
import LoginPage from "@/LoginPage";
import {
  Copy, Check, RotateCcw, Upload, X, Link2, Plus,
  FileText, FileSpreadsheet, Image, Loader2,
  ExternalLink, Compass, MapPin, Navigation, Clock,
  Trash2, ChevronRight, AlertTriangle,
  Shield, RefreshCw, Target, Zap, Users, DollarSign, Code,
  Route, Mail, TrendingUp, TrendingDown, Eye,
  MessageSquare, ArrowUpRight, Flame, Snowflake, ThermometerSun,
  BarChart3, FileDown, Play, LogOut, Inbox, HelpCircle,
} from "lucide-react";

// Auth handled via Bearer token in AuthContext

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/26a4f524-cd7c-42a9-b86d-e39aad365f48/images/0b1fa7d49a7483fea68a4b7508246ea399fd5589d2c5973459110f97b2902455.png";
const EMPTY_IMG = "https://static.prod-images.emergentagent.com/jobs/26a4f524-cd7c-42a9-b86d-e39aad365f48/images/13dc7cba5e9bef411bb7becb3dbf62add7b17ffc24fb0d345592736c824e29ff.png";

const DECIDE_TEMPLATES = [
  { icon: <Code className="w-4 h-4" />, label: "Tech", prompt: "Help me decide on our tech direction. Here's what we're building, our speed, costs, and scale goals:\n\n" },
  { icon: <DollarSign className="w-4 h-4" />, label: "Finance", prompt: "I have financial data but need a clear decision. Here's the revenue, costs, and cash flow situation:\n\n" },
  { icon: <Users className="w-4 h-4" />, label: "Manpower", prompt: "Help me make a people decision — roles, communication issues, training needs, or team structure:\n\n" },
  { icon: <TrendingUp className="w-4 h-4" />, label: "Sales", prompt: "Analyze our sales metrics and help me decide. Here's our top line, margins, EBITDA, and what the numbers might be hiding:\n\n" },
];
const SIGNAL_TEMPLATES = [
  { icon: <Mail className="w-4 h-4" />, label: "Investor Email", prompt: "Analyze this investor email thread for conversion signals:\n\n" },
  { icon: <Users className="w-4 h-4" />, label: "Customer Thread", prompt: "Analyze this customer conversation for buying signals:\n\n" },
  { icon: <MessageSquare className="w-4 h-4" />, label: "AI Transcript", prompt: "Analyze this AI-generated transcript/response for signals:\n\n" },
  { icon: <MessageSquare className="w-4 h-4" />, label: "Team Comms", prompt: "Analyze this internal team conversation (WhatsApp group, Slack, email) for alignment signals:\n\n" },
];

function getFileIcon(n) { const e = n.split(".").pop().toLowerCase(); if (["png","jpg","jpeg","gif","webp","bmp","svg"].includes(e)) return <Image className="w-4 h-4" />; if (["xlsx","csv","xls"].includes(e)) return <FileSpreadsheet className="w-4 h-4" />; if (["eml"].includes(e)) return <Mail className="w-4 h-4" />; if (["mp3","wav","m4a","ogg","webm"].includes(e)) return <MessageSquare className="w-4 h-4" />; return <FileText className="w-4 h-4" />; }
function timeAgo(iso) { const d = Date.now() - new Date(iso).getTime(); const m = Math.floor(d/60000); if (m < 1) return "just now"; if (m < 60) return `${m}m ago`; const h = Math.floor(m/60); if (h < 24) return `${h}h ago`; return `${Math.floor(h/24)}d ago`; }
function wordCount(t) { return t.trim().split(/\s+/).filter(Boolean).length; }

function ConfidenceBadge({ level }) {
  const c = { High: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", Medium: "bg-amber-500/20 text-amber-300 border-amber-500/30", Low: "bg-orange-500/20 text-orange-300 border-orange-500/30" };
  return <span className={`inline-flex items-center px-3 py-1 text-xs font-bold uppercase tracking-wider border rounded-md ${c[level] || c.Medium}`}><Shield className="w-3 h-3 mr-1.5" />{level}</span>;
}
function ScoreBadge({ score }) {
  const m = { hot: { icon: <Flame className="w-4 h-4" />, cls: "bg-orange-500/20 text-orange-300 border-orange-500/30", label: "HOT" }, warm: { icon: <ThermometerSun className="w-4 h-4" />, cls: "bg-amber-500/20 text-amber-300 border-amber-500/30", label: "WARM" }, cold: { icon: <Snowflake className="w-4 h-4" />, cls: "bg-blue-400/20 text-blue-300 border-blue-400/30", label: "COLD" } };
  const s = m[score] || m.warm;
  return <span className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider border rounded-lg ${s.cls}`}>{s.icon}{s.label}</span>;
}
function UrgencyDot({ level }) { const c = { high: "bg-orange-400", medium: "bg-amber-400", low: "bg-white/30" }; return <span className={`w-2 h-2 rounded-full ${c[level] || c.medium}`} />; }

/* ─── SIDEBAR ─── */
function HistorySidebar({ history, onSelect, onClear, onLogout, userName, onDelete, onHome, weeklyDecisions }) {
  return (
    <div className="w-full h-full flex flex-col bg-[#0a1520] border-r border-white/[0.08]">
      <div className="p-5 border-b border-white/[0.08]">
        <button onClick={onHome} className="flex items-center gap-2.5 mb-1 hover:opacity-80 transition-opacity"><Navigation className="w-5 h-5 text-[#C2440E]" /><span className="font-heading font-black text-xl tracking-tight text-white">DecideFast</span></button>
        <p className="font-mono text-[10px] text-white/85 tracking-[0.25em] uppercase mt-1">GPS for founders</p>
        {userName && (
          <div className="mt-3">
            <p className="font-mono text-[10px] text-white/95 truncate mb-2">{userName}</p>
            <div className="bg-[#1a1a3e] border border-[#6C5CE7]/25 rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-[#6C5CE7]" />
                <span className="text-white font-bold text-lg leading-none">{weeklyDecisions}</span>
                <span className="text-white text-xs">decisions this week</span>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-1">
        {history.length === 0 && <p className="text-white/95 text-xs text-center py-10 font-mono">No history yet</p>}
        {history.map((item) => (
          <div key={item.id} className="relative group">
            <button data-testid={`history-sidebar-item-${item.id}`} onClick={() => onSelect(item)}
              className="w-full text-left p-3.5 pr-10 rounded-lg transition-all duration-200 border hover:bg-white/[0.05] border-transparent">
              <div className="flex items-center gap-2 mb-1">
                {item.type === "signal" ? <Mail className="w-3 h-3 text-amber-400/70" /> : <Compass className="w-3 h-3 text-[#2dd4a0]/70" />}
                <span className="font-mono text-[9px] tracking-wider uppercase text-white/90">{item.type === "signal" ? "Signal" : "Decision"}</span>
                {item.conversation_tag && <span className="font-mono text-[8px] text-[#6C5CE7]/80 bg-[#6C5CE7]/10 px-1.5 py-0.5 rounded">{item.conversation_tag}</span>}
              </div>
              <p className="text-[13px] text-white/95 truncate font-medium group-hover:text-white">{item.type === "signal" ? (item.contact_name || item.input_preview) : (item.input_preview || item.recommendation)}</p>
              <div className="flex items-center gap-2 mt-1.5"><Clock className="w-3 h-3 text-white/90" /><span className="text-[10px] text-white/90 font-mono">{timeAgo(item.created_at)}</span></div>
            </button>
            <button data-testid={`delete-history-${item.id}`}
              onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
              className="absolute top-3.5 right-2.5 w-6 h-6 rounded-md flex items-center justify-center text-white/0 group-hover:text-white/90 hover:!text-orange-400 hover:bg-orange-400/10 transition-all">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      {history.length > 0 && (
        <div className="p-3 border-t border-white/[0.08]">
          <button data-testid="clear-history-button" onClick={onClear} className="w-full text-xs text-white/95 hover:text-orange-400 transition-colors py-2.5 font-mono flex items-center justify-center gap-1.5"><Trash2 className="w-3 h-3" /> Clear History</button>
        </div>
      )}
      <div className="p-3 border-t border-white/[0.08]">
        <button data-testid="logout-button" onClick={onLogout} className="w-full text-xs text-white/95 hover:text-white/90 transition-colors py-2.5 font-mono flex items-center justify-center gap-1.5"><LogOut className="w-3 h-3" /> Log Out</button>
      </div>
    </div>
  );
}

/* ─── DECISION CARDS ─── */
function DecisionCards({ decision, onShare, onReset, copied, onStepClick, onCommit, committedOption, conversationTag }) {
  const [tagCopied, setTagCopied] = useState(false);
  const copyTag = () => { if (conversationTag) { navigator.clipboard.writeText(conversationTag); setTagCopied(true); setTimeout(() => setTagCopied(false), 1500); } };
  return (
    <div className="w-full max-w-3xl space-y-4 animate-slide-up">
      {/* Conversation Tag */}
      {conversationTag && (
        <div data-testid="decision-card-tag" className="flex items-center justify-between px-4 py-3 bg-[#6C5CE7]/8 border border-[#6C5CE7]/20 rounded-xl">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-white/50 tracking-wider uppercase">Conversation</span>
            <span className="font-mono text-[13px] font-bold text-[#6C5CE7]">{conversationTag}</span>
          </div>
          <button data-testid="copy-decision-tag" onClick={copyTag}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#6C5CE7]/15 border border-[#6C5CE7]/25 rounded-lg text-[11px] text-[#6C5CE7] font-mono font-semibold hover:bg-[#6C5CE7]/25 transition-all active:scale-95">
            {tagCopied ? <><Check className="w-3 h-3" />Copied</> : <><Copy className="w-3 h-3" />Copy Tag</>}
          </button>
        </div>
      )}
      {/* Situation Summary */}
      {decision.situation_summary && (
        <div data-testid="card-situation-summary" className="flex items-start gap-3 px-5 py-4 bg-white/[0.03] border border-white/[0.06] rounded-xl">
          <FileDown className="w-4 h-4 text-white/95 shrink-0 mt-0.5" />
          <div>
            <span className="font-mono text-[9px] font-bold tracking-[0.2em] uppercase text-white/90 block mb-1">Situation Summary</span>
            <p className="text-white/85 text-[13px] leading-relaxed">{decision.situation_summary}</p>
          </div>
        </div>
      )}
      {/* Objective */}
      <div data-testid="card-objective" className="flex items-start gap-3 px-5 py-4 bg-white/[0.04] border border-white/[0.08] rounded-xl">
        <Target className="w-5 h-5 text-[#C2440E] shrink-0 mt-0.5" />
        <div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85 block mb-1">Objective</span>
          <p className="text-white/90 text-[15px] leading-relaxed font-medium">{decision.objective}</p></div>
      </div>
      {/* Recommendation */}
      <div data-testid="card-recommended-direction" onClick={() => onCommit("recommended")}
        className={`rounded-xl p-7 md:p-9 bg-[#15283E] border hover:-translate-y-0.5 transition-all cursor-pointer ${committedOption === "recommended" ? "border-[#2dd4a0] ring-2 ring-[#2dd4a0]/30" : "border-[#1D6B52]/40"}`}
        style={{ boxShadow: committedOption === "recommended" ? "inset 0 0 40px rgba(29,107,82,0.15), 0 0 0 1px rgba(45,212,160,0.4)" : "inset 0 0 40px rgba(29,107,82,0.08), 0 0 0 1px rgba(29,107,82,0.2)" }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5"><div className="w-9 h-9 rounded-lg bg-[#1D6B52]/25 flex items-center justify-center"><Compass className="w-5 h-5 text-[#2dd4a0]" /></div>
            <span className="font-mono text-[11px] font-bold tracking-[0.25em] uppercase text-[#2dd4a0]">Recommendation</span></div>
          <div className="flex items-center gap-2">
            {committedOption === "recommended" && <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold uppercase tracking-wider bg-[#2dd4a0]/20 text-[#2dd4a0] border border-[#2dd4a0]/30 rounded-md"><Check className="w-3 h-3" />Committed</span>}
            <ConfidenceBadge level={decision.confidence} />
          </div>
        </div>
        <h2 className="font-heading font-black text-[22px] md:text-[28px] lg:text-[32px] leading-[1.15] tracking-tight text-white">{decision.recommendation}</h2>
        {!committedOption && <p className="font-mono text-[10px] text-white/90 mt-4 tracking-wider">Click to commit to this decision</p>}
      </div>
      {/* Why This Wins */}
      <div data-testid="card-why-wins" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-6 hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
        <div className="flex items-center gap-2.5 mb-4"><div className="w-7 h-7 rounded-md bg-[#1D6B52]/15 flex items-center justify-center"><Zap className="w-4 h-4 text-[#2dd4a0]" /></div>
          <span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Why This Wins</span></div>
        <div className="space-y-3">{decision.why_this_wins.map((r, i) => (<div key={i} className="flex gap-3 items-start"><div className="w-1.5 h-1.5 rounded-full bg-[#2dd4a0] mt-2.5 shrink-0" /><p className="text-white/85 text-[15px] leading-relaxed">{r}</p></div>))}</div>
      </div>
      {/* Risk + Pivot */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="card-main-risk" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5 hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-md bg-orange-500/10 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-orange-400" /></div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Main Risk</span></div>
          <p className="text-white/85 text-[14px] leading-relaxed">{decision.main_risk}</p></div>
        <div data-testid="card-pivot" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5 hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-md bg-amber-500/10 flex items-center justify-center"><RefreshCw className="w-4 h-4 text-amber-400" /></div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Pivot If</span></div>
          <p className="text-white/85 text-[14px] leading-relaxed">{decision.recommendation_changes_if}</p></div>
      </div>
      {/* Alternatives */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {decision.alternatives.map((alt, i) => {
          const optKey = i === 0 ? "alternative_a" : "alternative_b";
          const isCommitted = committedOption === optKey;
          return (
          <div key={i} data-testid={`card-alternative-${i+1}`} onClick={() => onCommit(optKey)}
            className={`bg-[#15283E] border rounded-xl p-5 hover:-translate-y-0.5 transition-all cursor-pointer ${isCommitted ? "border-[#C2440E] ring-2 ring-[#C2440E]/30" : "border-white/[0.08]"}`}
            style={{ boxShadow: isCommitted ? "inset 0 0 20px rgba(194,68,14,0.1)" : "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-md bg-white/[0.06] flex items-center justify-center"><Route className="w-4 h-4 text-white/85" /></div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Alternative {i+1}</span></div>
              {isCommitted && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#C2440E]/20 text-[#C2440E] border border-[#C2440E]/30 rounded-md"><Check className="w-3 h-3" />Committed</span>}
            </div>
            <p className="text-white/95 text-[14px] leading-relaxed">{alt}</p>
          </div>);
        })}
      </div>
      {/* First Steps - CLICKABLE */}
      <div data-testid="card-first-steps" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-6 hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
        <div className="flex items-center gap-2.5 mb-5"><div className="w-7 h-7 rounded-md bg-[#C2440E]/15 flex items-center justify-center"><MapPin className="w-4 h-4 text-[#C2440E]" /></div>
          <span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">First Steps <span className="text-white/90 normal-case tracking-normal">(click to drill down)</span></span></div>
        <div className="space-y-3">
          {decision.first_steps.map((step, i) => (
            <button key={i} data-testid={`step-${i+1}`} onClick={() => onStepClick(step)}
              className="w-full flex gap-4 items-start group p-3 rounded-lg transition-all hover:bg-[#C2440E]/10 hover:border-[#C2440E]/20 border border-transparent cursor-pointer text-left">
              <div className="w-8 h-8 rounded-lg bg-[#C2440E]/15 flex items-center justify-center shrink-0 group-hover:bg-[#C2440E]/25 transition-colors"><span className="font-mono text-sm font-bold text-[#C2440E]">{i+1}</span></div>
              <p className="text-white/85 text-[15px] leading-relaxed pt-1 flex-1">{step}</p>
              <div className="flex items-center gap-1 shrink-0 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <Play className="w-3.5 h-3.5 text-[#C2440E]" /><span className="text-[10px] text-[#C2440E] font-mono">Drill down</span>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button data-testid="share-button" onClick={onShare} className="flex-1 bg-[#15283E] hover:bg-white/[0.08] border border-white/[0.1] hover:border-white/[0.2] text-white transition-all py-3.5 px-5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-95">
          {copied ? <Check className="w-4 h-4 text-[#2dd4a0]" /> : <Copy className="w-4 h-4" />}{copied ? "Copied" : "Share Decision"}</button>
        <button data-testid="new-decision-button" onClick={onReset} className="flex-1 bg-[#15283E] hover:bg-white/[0.08] border border-white/[0.1] hover:border-white/[0.2] text-white transition-all py-3.5 px-5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-95">
          <RotateCcw className="w-4 h-4" />New Decision</button>
      </div>
    </div>
  );
}

/* ─── SIGNAL CARDS ─── */
function SignalCards({ signal, onShare, onReset, copied, onDecideAbout }) {
  return (
    <div className="w-full max-w-3xl space-y-4 animate-slide-up">
      <div data-testid="signal-header" className="flex items-center justify-between px-5 py-4 bg-white/[0.04] border border-white/[0.08] rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#C2440E]/15 flex items-center justify-center">{signal.contact_type === "investor" ? <DollarSign className="w-5 h-5 text-[#C2440E]" /> : <Users className="w-5 h-5 text-[#C2440E]" />}</div>
          <div><p className="text-white font-bold text-lg">{signal.contact_name}</p>
            <div className="flex items-center gap-2 mt-0.5"><span className="font-mono text-[10px] tracking-wider uppercase text-white/95">{signal.contact_type}</span><span className="text-white/70">|</span><span className="font-mono text-[10px] tracking-wider uppercase text-white/95">{signal.deal_stage}</span><span className="text-white/70">|</span><UrgencyDot level={signal.urgency} /><span className="font-mono text-[10px] tracking-wider uppercase text-white/95">{signal.urgency}</span></div></div>
        </div>
        <ScoreBadge score={signal.signal_score} />
      </div>
      <div data-testid="signal-outcome" className="rounded-xl p-7 bg-[#15283E] border border-[#C2440E]/30 hover:-translate-y-0.5 transition-transform" style={{ boxShadow: "inset 0 0 30px rgba(194,68,14,0.06)" }}>
        <div className="flex items-center gap-2.5 mb-4"><div className="w-9 h-9 rounded-lg bg-[#C2440E]/20 flex items-center justify-center"><Target className="w-5 h-5 text-[#C2440E]" /></div><span className="font-mono text-[11px] font-bold tracking-[0.25em] uppercase text-[#C2440E]">Outcome Prediction</span></div>
        <p className="font-heading font-black text-[20px] md:text-[24px] leading-[1.2] tracking-tight text-white">{signal.outcome_prediction}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="signal-positive" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div className="flex items-center gap-2 mb-4"><div className="w-7 h-7 rounded-md bg-emerald-500/15 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-emerald-400" /></div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Positive Signals</span></div>
          {signal.positive_signals.length > 0 ? <div className="space-y-2.5">{signal.positive_signals.map((s, i) => (<div key={i} className="flex gap-2.5 items-start"><div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 shrink-0" /><p className="text-emerald-200/90 text-[13px] leading-relaxed">{s}</p></div>))}</div> : <p className="text-white/90 text-sm italic">None detected</p>}
        </div>
        <div data-testid="signal-negative" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
          <div className="flex items-center gap-2 mb-4"><div className="w-7 h-7 rounded-md bg-orange-500/15 flex items-center justify-center"><TrendingDown className="w-4 h-4 text-orange-400" /></div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Negative Signals</span></div>
          {signal.negative_signals.length > 0 ? <div className="space-y-2.5">{signal.negative_signals.map((s, i) => (<div key={i} className="flex gap-2.5 items-start"><div className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 shrink-0" /><p className="text-orange-200/90 text-[13px] leading-relaxed">{s}</p></div>))}</div> : <p className="text-white/90 text-sm italic">None detected</p>}
        </div>
      </div>
      <div data-testid="signal-insights" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
        <div className="flex items-center gap-2 mb-4"><div className="w-7 h-7 rounded-md bg-white/[0.06] flex items-center justify-center"><Eye className="w-4 h-4 text-white/85" /></div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Key Insights</span></div>
        <div className="space-y-3">{signal.key_insights.map((ins, i) => (<div key={i} className="flex gap-3 items-start"><div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0" /><p className="text-white/85 text-[14px] leading-relaxed">{ins}</p></div>))}</div>
      </div>
      <div data-testid="signal-action" className="bg-[#15283E] border border-[#1D6B52]/30 rounded-xl p-6" style={{ boxShadow: "inset 0 0 20px rgba(29,107,82,0.06)" }}>
        <div className="flex items-center gap-2.5 mb-3"><div className="w-7 h-7 rounded-md bg-[#1D6B52]/20 flex items-center justify-center"><ArrowUpRight className="w-4 h-4 text-[#2dd4a0]" /></div><span className="font-mono text-[10px] font-bold tracking-[0.25em] uppercase text-[#2dd4a0]">Recommended Next Move</span></div>
        <p className="text-white font-semibold text-[16px] leading-relaxed">{signal.recommended_action}</p>
      </div>
      <div data-testid="signal-response" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
        <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-md bg-white/[0.06] flex items-center justify-center"><MessageSquare className="w-4 h-4 text-white/85" /></div><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Suggested Response</span></div>
        <p className="text-white/95 text-[14px] leading-relaxed font-mono whitespace-pre-wrap">{signal.suggested_response}</p>
      </div>
      <div className="flex gap-3 pt-1">
        <button data-testid="decide-about-signal" onClick={() => onDecideAbout(signal)} className="flex-1 bg-[#C2440E] hover:bg-[#A6390C] text-white transition-all py-3.5 text-sm font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 shadow-lg shadow-[#C2440E]/20"><Compass className="w-4 h-4" />Decide About This</button>
        <button data-testid="share-signal-button" onClick={onShare} className="flex-1 bg-[#15283E] hover:bg-white/[0.08] border border-white/[0.1] hover:border-white/[0.2] text-white transition-all py-3.5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-95">{copied ? <Check className="w-4 h-4 text-[#2dd4a0]" /> : <Copy className="w-4 h-4" />}{copied ? "Copied" : "Share"}</button>
        <button data-testid="new-signal-button" onClick={onReset} className="flex-1 bg-[#15283E] hover:bg-white/[0.08] border border-white/[0.1] hover:border-white/[0.2] text-white transition-all py-3.5 text-sm font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-95"><RotateCcw className="w-4 h-4" />New</button>
      </div>
    </div>
  );
}

/* ─── PIPELINE DASHBOARD ─── */
function PipelineDashboard({ pipeline, onContactClick }) {
  const columns = [
    { key: "hot", label: "Hot", color: "text-orange-400", border: "border-orange-500/20", bg: "bg-orange-500/5", icon: <Flame className="w-4 h-4" /> },
    { key: "warm", label: "Warm", color: "text-amber-400", border: "border-amber-500/20", bg: "bg-amber-500/5", icon: <ThermometerSun className="w-4 h-4" /> },
    { key: "cold", label: "Cold", color: "text-blue-300", border: "border-blue-400/20", bg: "bg-blue-400/5", icon: <Snowflake className="w-4 h-4" /> },
  ];
  const total = (pipeline.hot?.length || 0) + (pipeline.warm?.length || 0) + (pipeline.cold?.length || 0);
  if (total === 0) {
    return (
      <div className="w-full max-w-4xl flex flex-col items-center gap-6 text-center py-16 animate-fade-in">
        <BarChart3 className="w-12 h-12 text-white/60" />
        <p className="font-mono text-sm text-white/90 tracking-widest uppercase">No signals analyzed yet</p>
        <p className="text-white/70 text-xs max-w-xs">Switch to Signal Tracker, paste email threads, and your pipeline will build automatically.</p>
      </div>
    );
  }
  return (
    <div className="w-full max-w-5xl animate-slide-up">
      <div className="flex items-center gap-3 mb-6">
        <BarChart3 className="w-5 h-5 text-[#C2440E]" />
        <h2 className="font-heading font-black text-xl text-white">Deal Pipeline</h2>
        <span className="font-mono text-[10px] text-white/95 tracking-wider">{total} contacts</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map((col) => (
          <div key={col.key} className={`rounded-xl border ${col.border} ${col.bg} p-4`}>
            <div className="flex items-center gap-2 mb-4">
              <span className={col.color}>{col.icon}</span>
              <span className={`font-mono text-xs font-bold tracking-wider uppercase ${col.color}`}>{col.label}</span>
              <span className="font-mono text-[10px] text-white/90 ml-auto">{pipeline[col.key]?.length || 0}</span>
            </div>
            <div className="space-y-2">
              {(pipeline[col.key] || []).map((c) => (
                <button key={c.id} data-testid={`pipeline-card-${c.id}`} onClick={() => onContactClick(c.id)}
                  className="w-full text-left p-3.5 bg-[#15283E] border border-white/[0.06] rounded-lg hover:border-white/[0.15] transition-all group">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white/90 text-sm font-semibold truncate">{c.contact_name}</p>
                    <span className="font-mono text-[9px] text-white/90 uppercase">{c.contact_type}</span>
                  </div>
                  <p className="text-white/85 text-xs truncate">{c.deal_stage}</p>
                  {c.outcome_prediction && <p className="text-white/95 text-[11px] mt-1.5 truncate leading-relaxed">{c.outcome_prediction}</p>}
                  <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronRight className="w-3 h-3 text-[#C2440E]" /><span className="text-[10px] text-[#C2440E] font-mono">View details</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── MAIN APP ─── */
function DecideFastApp() {
  const { user, logout } = useAuth();
  const userName = user?.name || user?.email || "";
  const [mode, setMode] = useState("signal");
  const [inputText, setInputText] = useState("");
  const [files, setFiles] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState("");
  const [decision, setDecision] = useState(null);
  const [signal, setSignal] = useState(null);
  const [analyzedSources, setAnalyzedSources] = useState([]);
  const [conversationTag, setConversationTag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [history, setHistory] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [committedOption, setCommittedOption] = useState(null);
  const [weeklyDecisions, setWeeklyDecisions] = useState(0);

  const [autoSubmitPending, setAutoSubmitPending] = useState(false);

  // Session ID for decision tracking (persist across refreshes)
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem("decidefast_session_id");
    if (!sid) { sid = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2); localStorage.setItem("decidefast_session_id", sid); }
    return sid;
  });
  const [pipeline, setPipeline] = useState({ hot: [], warm: [], cold: [] });
  const [autoFiles, setAutoFiles] = useState([]);
  const [inboxItems, setInboxItems] = useState([]);
  const [webhookStatus, setWebhookStatus] = useState({ connected: false, webhook_key: null });
  const [showConnectPopup, setShowConnectPopup] = useState(false);
  const [foundersBrief, setFoundersBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);

  useEffect(() => { loadHistory(); loadWeeklyDecisions(); loadFoundersBrief(); }, []);
  useEffect(() => { if (mode === "pipeline") loadPipeline(); }, [mode]);
  useEffect(() => { if (mode === "inbox") { loadInbox(); loadWebhookStatus(); } }, [mode]);
  // Auto-submit when content is loaded (WhatsApp, audio)
  useEffect(() => {
    if (autoSubmitPending && !loading) {
      setAutoSubmitPending(false);
      const btn = document.querySelector('[data-testid="decide-submit-button"]');
      if (btn && !btn.disabled) btn.click();
    }
  }, [autoSubmitPending, loading]);

  const loadHistory = async () => { try { const r = await axios.get(`${API}/history`); setHistory(r.data); } catch {} };
  const loadPipeline = async () => { try { const r = await axios.get(`${API}/pipeline`); setPipeline(r.data); } catch {} };
  const loadInbox = async () => { try { const r = await axios.get(`${API}/inbox`); setInboxItems(r.data); } catch {} };
  const loadWebhookStatus = async () => { try { const r = await axios.get(`${API}/webhook/status`); setWebhookStatus(r.data); } catch {} };
  const loadWeeklyDecisions = async () => { try { const r = await axios.get(`${API}/decisions/metrics`); setWeeklyDecisions(r.data.total_decisions || 0); } catch {} };
  const handleWebhookConnect = async () => { try { const r = await axios.post(`${API}/webhook/connect`); setWebhookStatus({ connected: true, webhook_key: r.data.webhook_key }); } catch {} };
  const handleWebhookDisconnect = async () => { try { await axios.post(`${API}/webhook/disconnect`); setWebhookStatus({ connected: false, webhook_key: null }); setShowConnectPopup(false); } catch {} };
  const loadFoundersBrief = async () => { setBriefLoading(true); try { const r = await axios.get(`${API}/founders-brief`); setFoundersBrief(r.data); } catch {} finally { setBriefLoading(false); } };

  const handleFileUpload = useCallback(async (fileList) => {
    const audioExts = ["mp3","mp4","mpeg","mpga","m4a","wav","webm","ogg"];
    const blockedExts = ["exe","bat","cmd","com","msi","scr","pif","vbs","vbe","jse","ws","wsf","ps1","sh","bash","dll","sys","drv","ocx","cpl","inf","reg","lnk","app","bin","apk","ipa","deb","rpm"];
    for (const file of Array.from(fileList)) {
      const ext = file.name.split(".").pop().toLowerCase();
      // WhatsApp export detection
      if (ext === "txt" && file.name.toLowerCase().includes("whatsapp")) {
        await handleWhatsAppUpload(file);
        continue;
      }
      // Block dangerous files
      if (blockedExts.includes(ext)) { setError(`${file.name}: blocked for security`); continue; }
      // Audio files → transcribe
      if (audioExts.includes(ext)) {
        if (file.size > 25 * 1024 * 1024) { setError(`${file.name} too large for audio (max 25MB)`); continue; }
        await handleAudioUpload(file);
        continue;
      }
      // All other files → upload & extract text
      if (file.size > 10 * 1024 * 1024) { setError(`${file.name} too large (max 10MB)`); continue; }
      const tempId = Date.now() + Math.random();
      setFiles(prev => [...prev, { id: tempId, name: file.name, uploading: true, extractedText: "" }]);
      const fd = new FormData(); fd.append("file", file);
      try {
        const r = await axios.post(`${API}/upload`, fd);
        setFiles(prev => prev.map(f => f.id === tempId ? { ...f, id: r.data.file_id, uploading: false, extractedText: r.data.extracted_text } : f));
        setError("");
      } catch { setFiles(prev => prev.filter(f => f.id !== tempId)); setError(`Upload failed: ${file.name}`); }
    }
  }, []);

  const handleDrop = useCallback((e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files.length) handleFileUpload(e.dataTransfer.files); }, [handleFileUpload]);
  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));
  const removeAutoFile = (id) => setAutoFiles(prev => prev.filter(f => f.id !== id));

  const addLink = async () => {
    let url = linkInput.trim(); if (!url) return;
    if (!url.startsWith("http://") && !url.startsWith("https://")) url = "https://" + url;
    setLinkInput("");
    const tempId = Date.now();
    setLinks(prev => [...prev, { id: tempId, url, title: url, content: "", loading: true, success: null }]);
    try { const r = await axios.post(`${API}/scrape-url`, { url }); setLinks(prev => prev.map(l => l.id === tempId ? { ...l, title: r.data.title, content: r.data.content, loading: false, success: r.data.success } : l)); }
    catch { setLinks(prev => prev.map(l => l.id === tempId ? { ...l, loading: false, success: false } : l)); }
  };
  const removeLink = (id) => setLinks(prev => prev.filter(l => l.id !== id));

  // Auto-convert >100 words to attached context
  const processAutoFile = (text) => {
    if (wordCount(text) > 100) {
      const newAutoFile = { id: Date.now(), name: `Pasted text (${wordCount(text)} words)`, extractedText: text };
      setAutoFiles(prev => [...prev, newAutoFile]);
      return true;
    }
    return false;
  };

  const handleSubmit = async () => {
    if (!inputText.trim() && files.length === 0 && links.length === 0 && autoFiles.length === 0) return;
    setLoading(true); setError(""); setDecision(null); setSignal(null);

    // Auto-convert long text
    let textToSend = inputText;
    let extraContexts = [];
    if (wordCount(inputText) > 100) {
      extraContexts.push(inputText);
      textToSend = `Analyze the attached pasted text (${wordCount(inputText)} words)`;
    }

    const fc = [
      ...files.filter(f => !f.uploading && f.extractedText).map(f => f.extractedText),
      ...autoFiles.map(f => f.extractedText),
      ...extraContexts,
    ];
    const lc = links.filter(l => l.success && l.content).map(l => ({ url: l.url, title: l.title, content: l.content }));

    // Build a meaningful input_text if user only provided files/links without text
    let finalText = textToSend;
    if (!finalText.trim()) {
      const parts = [];
      if (fc.length > 0) parts.push(`${fc.length} document(s)`);
      if (lc.length > 0) parts.push(lc.map(l => `"${l.title}"`).join(", "));
      finalText = parts.length > 0
        ? `Analyze the provided reference material: ${parts.join(" and ")}. Extract key signals and make a decision based on the content.`
        : "Use the uploaded documents and links as context.";
    }

    // Capture analyzed sources before clearing
    const sources = [
      ...files.filter(f => !f.uploading && f.extractedText).map(f => ({ type: "file", name: f.name })),
      ...autoFiles.map(f => ({ type: "text", name: f.name })),
      ...links.filter(l => !l.loading).map(l => ({ type: "link", name: l.title || l.url, url: l.url, success: l.success })),
    ];

    // Get previous summaries for context chaining
    let prevSummaries = null;
    try { const sr = await axios.get(`${API}/summaries`); if (sr.data.length > 0) prevSummaries = sr.data; } catch {}

    const payload = {
      input_text: finalText,
      file_contexts: fc.length ? fc : null,
      link_contexts: lc.length ? lc : null,
      ...(prevSummaries && { previous_summaries: prevSummaries }),
      ...(conversationTag && { conversation_tag: conversationTag }),
    };
    // Both tabs use chained signal→decide endpoint
    const endpoint = `${API}/decide-with-signals`;
    try {
      const r = await axios.post(endpoint, payload);
      // Chained response: { signal, decision, conversation_tag }
      setSignal(r.data.signal);
      setDecision(r.data.decision);
      setConversationTag(r.data.conversation_tag);
      setAnalyzedSources(sources);
      loadHistory();
      loadFoundersBrief();
      // Clear input after successful submission
      setInputText("");
      setFiles([]);
      setLinks([]);
      setAutoFiles([]);
      setLinkInput("");
    } catch (err) {
      if (err.response?.status === 429) setError("Rate limit reached. Wait a moment.");
      else setError("Failed. Try again.");
    } finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit(); };

  // Handle paste - auto detect >100 words
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    if (wordCount(pasted) > 100 && wordCount(inputText) <= 10) {
      e.preventDefault();
      const af = { id: Date.now(), name: `Pasted text (${wordCount(pasted)} words)`, extractedText: pasted };
      setAutoFiles(prev => [...prev, af]);
      setInputText(`Analyze the pasted text (${wordCount(pasted)} words)`);
    }
  };

  const handleShare = async () => {
    let text = "";
    if (decision) {
      text = `DECIDEFAST\n\nSITUATION: ${decision.situation_summary}\nOBJECTIVE: ${decision.objective}\nRECOMMENDATION: ${decision.recommendation}\n\nWHY: ${decision.why_this_wins.map(r=>`- ${r}`).join('\n')}\n\nRISK: ${decision.main_risk}\nCONFIDENCE: ${decision.confidence}\nPIVOT IF: ${decision.recommendation_changes_if}\n\nALTERNATIVES:\n1. ${decision.alternatives[0]}\n2. ${decision.alternatives[1]}\n\nSTEPS:\n1. ${decision.first_steps[0]}\n2. ${decision.first_steps[1]}\n3. ${decision.first_steps[2]}`;
    } else if (signal) {
      text = `DECIDEFAST SIGNAL\n\nContact: ${signal.contact_name} (${signal.contact_type})\nSignal: ${signal.signal_score.toUpperCase()} | Stage: ${signal.deal_stage}\n\nOUTCOME: ${signal.outcome_prediction}\n\n+ ${signal.positive_signals.join('\n+ ')}\n\n- ${signal.negative_signals.join('\n- ')}\n\nACTION: ${signal.recommended_action}`;
    }
    try { await navigator.clipboard.writeText(text); } catch { const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const handleReset = () => { setDecision(null); setSignal(null); setInputText(""); setFiles([]); setLinks([]); setAutoFiles([]); setLinkInput(""); setError(""); setCopied(false); setCommittedOption(null); setAnalyzedSources([]); setConversationTag(null); };

  // Clickable step → drill down as new decision
  const handleStepClick = (step) => {
    setDecision(null); setSignal(null);
    setInputText(`I need to execute this step: "${step}"\n\nBreak this down into a detailed plan. What specifically should I do, what tools/resources do I need, and what are the risks?`);
    setMode("decide");
  };

  // Signal → Decision bridge
  const handleDecideAboutSignal = (sig) => {
    setSignal(null); setDecision(null);
    const ctx = `Based on my signal analysis:\n\nContact: ${sig.contact_name} (${sig.contact_type})\nSignal Score: ${sig.signal_score.toUpperCase()}\nDeal Stage: ${sig.deal_stage}\nOutcome: ${sig.outcome_prediction}\n\nPositive signals: ${sig.positive_signals.join('; ')}\nNegative signals: ${sig.negative_signals.join('; ')}\nInsights: ${sig.key_insights.join('; ')}\nRecommended action: ${sig.recommended_action}\n\nHelp me decide: What is my best course of action with ${sig.contact_name}? Should I invest more time/resources, pivot my approach, or move on?`;
    setInputText(ctx);
    setMode("decide");
  };

  // Decision commit tracking (NSM)
  const handleCommitDecision = async (option) => {
    if (committedOption === option) return;
    const wasAlreadyCommitted = !!committedOption;
    setCommittedOption(option);
    try {
      const r = await axios.post(`${API}/decision`, {
        session_id: sessionId,
        selected_option: option,
        timestamp: Date.now() / 1000,
        ...(conversationTag && { conversation_tag: conversationTag }),
      });
      // Only increment counter for genuinely new commits
      if (r.data.is_new_decision && !wasAlreadyCommitted) {
        setWeeklyDecisions(prev => prev + 1);
      }
    } catch {}
  };

  // Audio transcription handler
  const handleAudioUpload = async (file) => {
    const tempId = Date.now() + Math.random();
    setFiles(prev => [...prev, { id: tempId, name: file.name, uploading: true, extractedText: "", isAudio: true }]);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/transcribe-audio`, fd);
      setFiles(prev => prev.map(f => f.id === tempId ? { ...f, uploading: false, extractedText: r.data.transcript, isAudio: true } : f));
      setInputText(`Analyze this voice note from ${file.name}`);
      setError("");
      // Auto-trigger signal→decision chain
      setAutoSubmitPending(true);
    } catch (err) {
      setFiles(prev => prev.filter(f => f.id !== tempId));
      setError(err.response?.data?.detail || `Failed to transcribe ${file.name}`);
    }
  };

  // WhatsApp chat parsing
  const [whatsappContacts, setWhatsappContacts] = useState([]);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [showContactPicker, setShowContactPicker] = useState(false);

  const handleWhatsAppUpload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await axios.post(`${API}/parse-whatsapp`, fd);
      setWhatsappContacts(r.data.contacts);
      setWhatsappMessages(r.data.messages);
      setShowContactPicker(true);
      setError("");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to parse WhatsApp export");
    }
  };

  const handleSelectWhatsAppContact = async (contactName) => {
    try {
      const r = await axios.post(`${API}/filter-whatsapp`, { messages: whatsappMessages, contact_name: contactName });
      const threadText = r.data.thread_text;
      const af = { id: Date.now(), name: `WhatsApp: ${contactName} (${r.data.message_count} msgs)`, extractedText: threadText };
      setAutoFiles(prev => [...prev, af]);
      setInputText(`Analyze WhatsApp conversation with ${contactName} (${r.data.message_count} messages)`);
      setShowContactPicker(false);
      setWhatsappContacts([]);
      setWhatsappMessages([]);
      // Auto-trigger signal→decision chain
      setAutoSubmitPending(true);
    } catch { setError("Failed to filter messages"); }
  };

  const handleSelectHistory = async (item) => {
    try {
      const r = await axios.get(`${API}/history/${item.id}`);
      const d = r.data;
      if (d.type === "signal") {
        setMode("signal"); setDecision(null);
        setSignal({ contact_name: d.contact_name, contact_type: d.contact_type, signal_score: d.signal_score, deal_stage: d.deal_stage, positive_signals: d.positive_signals || [], negative_signals: d.negative_signals || [], key_insights: d.key_insights || [], recommended_action: d.recommended_action || "", urgency: d.urgency || "medium", outcome_prediction: d.outcome_prediction || "", suggested_response: d.suggested_response || "" });
      } else {
        setMode("decide"); setSignal(null);
        setDecision({ objective: d.objective || "", situation_summary: d.situation_summary || "", recommendation: d.recommendation || d.recommended_direction || "", why_this_wins: d.why_this_wins || [], main_risk: d.main_risk || "", confidence: d.confidence || "Medium", recommendation_changes_if: d.recommendation_changes_if || "", alternatives: d.alternatives || [], first_steps: d.first_steps || [] });
      }
      // Set tag so user can continue this conversation
      setConversationTag(d.conversation_tag || null);
      setInputText("");
      setCommittedOption(null);
      setSidebarOpen(false);
    } catch {}
  };

  const handlePipelineContactClick = async (id) => {
    try {
      const r = await axios.get(`${API}/history/${id}`);
      const d = r.data;
      setMode("signal"); setDecision(null);
      setSignal({ contact_name: d.contact_name, contact_type: d.contact_type, signal_score: d.signal_score, deal_stage: d.deal_stage, positive_signals: d.positive_signals || [], negative_signals: d.negative_signals || [], key_insights: d.key_insights || [], recommended_action: d.recommended_action || "", urgency: d.urgency || "medium", outcome_prediction: d.outcome_prediction || "", suggested_response: d.suggested_response || "" });
    } catch {}
  };

  const handleClearHistory = async () => { try { await axios.delete(`${API}/history`); setHistory([]); setPipeline({ hot: [], warm: [], cold: [] }); } catch {} };
  const handleDeleteHistoryItem = async (id) => {
    try { await axios.delete(`${API}/history/${id}`); setHistory(prev => prev.filter(h => h.id !== id)); if (decision || signal) { setDecision(null); setSignal(null); } } catch {}
  };
  const handleLogout = async () => { await logout(); };
  const handleGoHome = () => { setDecision(null); setSignal(null); setMode("signal"); setInputText(""); setError(""); };

  const switchMode = (m) => { if (m === mode) return; setMode(m); setDecision(null); setSignal(null); setError(""); if (m !== "pipeline") setInputText(""); };

  const hasInput = inputText.trim() || files.some(f => !f.uploading) || links.length > 0 || autoFiles.length > 0;
  const anyUploading = files.some(f => f.uploading) || links.some(l => l.loading);
  const templates = mode === "signal" ? SIGNAL_TEMPLATES : DECIDE_TEMPLATES;
  const hasOutput = decision || signal;

  return (
    <div className="h-screen w-full flex overflow-hidden relative">
      <div className="fixed inset-0 bg-cover bg-center opacity-[0.06] pointer-events-none z-0" style={{ backgroundImage: `url(${BG_IMAGE})` }} />
      <button data-testid="toggle-sidebar-mobile" onClick={() => setSidebarOpen(!sidebarOpen)} className="md:hidden fixed top-4 left-4 z-50 w-10 h-10 rounded-lg bg-[#15283E] border border-white/[0.15] flex items-center justify-center text-white"><Clock className="w-4 h-4" /></button>
      <div className={`fixed md:relative z-40 h-full w-[280px] shrink-0 transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}><HistorySidebar history={history} onSelect={handleSelectHistory} onClear={handleClearHistory} onLogout={handleLogout} userName={userName} onDelete={handleDeleteHistoryItem} onHome={handleGoHome} weeklyDecisions={weeklyDecisions} /></div>
      {sidebarOpen && <div className="md:hidden fixed inset-0 z-30 bg-black/60" onClick={() => setSidebarOpen(false)} />}

      {/* INPUT PANE — hidden in pipeline mode on desktop */}
      {mode !== "pipeline" && mode !== "inbox" && (
        <div className="flex-1 h-full overflow-y-auto custom-scrollbar relative z-10 border-r border-white/[0.06]">
          <div className="p-6 md:p-10 flex flex-col gap-5 max-w-2xl mx-auto">
            <div className="md:hidden h-6" />

            {/* Mode Tabs — 4 tabs, Signal first */}
            <div className="flex bg-[#0a1520] border border-white/[0.08] rounded-xl p-1 gap-1">
              <button data-testid="mode-signal" onClick={() => switchMode("signal")} className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${mode === "signal" ? "bg-[#15283E] text-white border border-white/[0.1]" : "text-white/95 hover:text-white/90"}`}><Mail className="w-4 h-4" />Signals</button>
              <button data-testid="mode-decide" onClick={() => switchMode("decide")} className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${mode === "decide" ? "bg-[#15283E] text-white border border-white/[0.1]" : "text-white/95 hover:text-white/90"}`}><Compass className="w-4 h-4" />Decide</button>
              <button data-testid="mode-pipeline" onClick={() => switchMode("pipeline")} className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all ${mode === "pipeline" ? "bg-[#15283E] text-white border border-white/[0.1]" : "text-white/95 hover:text-white/90"}`}><BarChart3 className="w-4 h-4" />Pipeline</button>
              <button data-testid="mode-inbox" onClick={() => switchMode("inbox")} className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all relative ${mode === "inbox" ? "bg-[#15283E] text-white border border-white/[0.1]" : "text-white/95 hover:text-white/90"}`}><Inbox className="w-4 h-4" />Inbox{inboxItems.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#C2440E] rounded-full text-[9px] font-bold flex items-center justify-center">{inboxItems.length}</span>}</button>
            </div>

            <div><label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/95 mb-2 block">Quick start</label>
              <div className="grid grid-cols-2 gap-2">{templates.map((t, i) => (<button key={i} data-testid={`template-${i}`} onClick={() => setInputText(t.prompt)} className="flex items-center gap-2.5 px-3.5 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[13px] text-white/90 hover:text-white hover:bg-white/[0.08] hover:border-white/[0.15] transition-all text-left"><span className="text-[#C2440E]">{t.icon}</span>{t.label}</button>))}</div></div>

            {/* Continue conversation banner */}
            {conversationTag && (decision || signal) && (
              <div data-testid="continue-conversation" className="flex items-center gap-2 px-3 py-2.5 bg-[#6C5CE7]/10 border border-[#6C5CE7]/20 rounded-xl">
                <span className="font-mono text-[11px] text-[#6C5CE7] font-bold">{conversationTag}</span>
                <span className="text-white/50 text-[11px]">Add new context below to update this decision</span>
                <button onClick={() => { setConversationTag(null); setDecision(null); setSignal(null); setCommittedOption(null); }} className="ml-auto text-white/40 hover:text-white/70 text-[10px] font-mono">New conversation</button>
              </div>
            )}

            <div><label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/95 mb-2 block">{mode === "signal" ? "Paste email thread, transcript, or conversation" : "Describe your situation"}</label>
              <textarea data-testid="prompt-input-textarea" value={inputText} onChange={e => setInputText(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste}
                placeholder={conversationTag && (decision || signal) ? `Continue ${conversationTag} — add new data, context, or ask a follow-up...` : mode === "signal" ? "Paste the email thread, AI transcript, or conversation log here..." : "Paste your situation, thoughts, or problem... (Use @DFxxxx to reference a past conversation)"}
                className="w-full bg-[#15283E] border border-white/[0.1] focus:border-[#C2440E]/50 focus:ring-1 focus:ring-[#C2440E]/30 transition-all p-4 rounded-xl min-h-[140px] resize-none text-[15px] text-white/90 leading-relaxed placeholder:text-white/40" />
              <div className="flex justify-between mt-1.5"><span className="font-mono text-[10px] text-white/90">{inputText.length > 0 && `${wordCount(inputText)} words`}</span><span className="font-mono text-[10px] text-white/90">Ctrl+Enter</span></div></div>

            {/* Auto-converted files */}
            {autoFiles.length > 0 && (
              <div className="space-y-1.5">
                <label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-amber-400/60 mb-1 block">Auto-attached (100+ words)</label>
                {autoFiles.map(f => (
                  <div key={f.id} className="flex items-center gap-2.5 px-3 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm">
                    <FileDown className="w-4 h-4 text-amber-400" /><span className="flex-1 truncate text-amber-200/80">{f.name}</span>
                    <button onClick={() => removeAutoFile(f.id)} className="text-white/90 hover:text-white/90"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}

            <div><label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/95 mb-2 block">Attach documents</label>
              <div data-testid="file-upload-zone" onDragOver={e => { e.preventDefault(); setDragActive(true); }} onDragLeave={() => setDragActive(false)} onDrop={handleDrop}
                onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.multiple = true; inp.onchange = e => handleFileUpload(e.target.files); inp.click(); }}
                className={`w-full border-2 border-dashed rounded-xl transition-all p-5 flex flex-col items-center justify-center gap-2 cursor-pointer ${dragActive ? "border-[#C2440E] bg-[#C2440E]/5" : "border-white/[0.1] hover:border-white/[0.2] bg-white/[0.02]"}`}>
                <Upload className="w-5 h-5 text-white/90" /><p className="text-sm text-white/90">Drop files or <span className="text-[#C2440E] font-semibold">browse</span></p><p className="text-[10px] text-white/90 font-mono">Docs, emails, audio, images, WhatsApp exports — anything safe</p></div>
              {files.length > 0 && (<div className="mt-2 space-y-1.5">{files.map(f => (<div key={f.id} data-testid={`uploaded-file-${f.id}`} className="flex items-center gap-2.5 px-3 py-2.5 bg-[#15283E] border border-white/[0.08] rounded-lg text-sm">{f.uploading ? <Loader2 className="w-4 h-4 text-white/95 animate-spin" /> : getFileIcon(f.name)}<span className="flex-1 truncate text-white/90">{f.name}</span>{!f.uploading && <button onClick={() => removeFile(f.id)} className="text-white/90 hover:text-white/90"><X className="w-3.5 h-3.5" /></button>}</div>))}</div>)}</div>

            <div><label className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/95 mb-2 block">Reference links</label>
              <div className="flex bg-[#15283E] border border-white/[0.1] rounded-xl focus-within:border-[#C2440E]/50 focus-within:ring-1 focus-within:ring-[#C2440E]/30 transition-all overflow-hidden">
                <div className="flex items-center pl-3.5 text-white/90"><Link2 className="w-4 h-4" /></div>
                <input data-testid="link-input-field" type="text" value={linkInput} onChange={e => setLinkInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addLink(); }} placeholder="Paste a URL, press Enter" className="flex-1 px-3 py-3 text-sm bg-transparent text-white/90 outline-none placeholder:text-white/40" />
                <button data-testid="add-link-button" onClick={addLink} disabled={!linkInput.trim()} className="px-3.5 text-white/90 hover:text-[#C2440E] transition-colors disabled:opacity-20"><Plus className="w-4 h-4" /></button></div>
              {links.length > 0 && (<div className="mt-2 space-y-1.5">{links.map(l => (<div key={l.id} data-testid={`added-link-${l.id}`} className="flex items-center gap-2.5 px-3 py-2.5 bg-[#15283E] border border-white/[0.08] rounded-lg text-sm">{l.loading ? <Loader2 className="w-4 h-4 text-white/95 animate-spin" /> : l.success ? <ExternalLink className="w-4 h-4 text-[#2dd4a0]" /> : <ExternalLink className="w-4 h-4 text-orange-400" />}<span className="flex-1 truncate text-white/90" title={l.url}>{l.title || l.url}</span><button onClick={() => removeLink(l.id)} className="text-white/90 hover:text-white/90"><X className="w-3.5 h-3.5" /></button></div>))}</div>)}</div>

            {/* WhatsApp Contact Picker */}
            {showContactPicker && whatsappContacts.length > 0 && (
              <div data-testid="whatsapp-contact-picker" className="bg-[#15283E] border border-white/[0.1] rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#2dd4a0]" /><span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/85">Select contact to analyze</span></div>
                  <button onClick={() => { setShowContactPicker(false); setWhatsappContacts([]); }} className="text-white/90 hover:text-white/85"><X className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {whatsappContacts.map((c, i) => (
                    <button key={i} data-testid={`whatsapp-contact-${i}`} onClick={() => handleSelectWhatsAppContact(c)}
                      className="flex items-center gap-2.5 px-3.5 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-[13px] text-white/95 hover:text-white hover:bg-white/[0.08] hover:border-white/[0.15] transition-all text-left">
                      <Users className="w-4 h-4 text-[#C2440E] shrink-0" /><span className="truncate">{c}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {error && <p data-testid="error-message" className="font-mono text-xs text-orange-400 font-medium">{error}</p>}

            <button data-testid="decide-submit-button" onClick={handleSubmit} disabled={!hasInput || loading || anyUploading}
              className="w-full bg-[#C2440E] hover:bg-[#A6390C] text-white font-bold py-4 text-lg rounded-xl flex items-center justify-center gap-2.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.97] shadow-lg shadow-[#C2440E]/20">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Analyzing...</> : mode === "signal" ? <><Mail className="w-5 h-5" /> Analyze & Decide</> : <><Navigation className="w-5 h-5" /> DecideFast</>}</button>
            <p className="text-[11px] text-white/90 text-center leading-relaxed">Your inputs are yours. We only learn how founders think — Not what they plan.</p>
          </div>
        </div>
      )}

      {/* OUTPUT PANE — FIXED SCROLL */}
      <div className={`${mode === "pipeline" || mode === "inbox" ? "flex-1" : "hidden md:flex flex-1"} h-full overflow-y-auto custom-scrollbar relative z-10`}>
        <div className="w-full p-6 md:p-10 pt-8 md:pt-12 pb-24"
          style={{ minHeight: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
          {mode === "pipeline" ? (
            <div className="w-full max-w-5xl">
              <div className="flex bg-[#0a1520] border border-white/[0.08] rounded-xl p-1 gap-1 mb-8 max-w-lg">
                <button onClick={() => switchMode("signal")} className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 text-white/95 hover:text-white/90 transition-all"><Mail className="w-4 h-4" />Signals</button>
                <button onClick={() => switchMode("decide")} className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 text-white/95 hover:text-white/90 transition-all"><Compass className="w-4 h-4" />Decide</button>
                <button className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 bg-[#15283E] text-white border border-white/[0.1]"><BarChart3 className="w-4 h-4" />Pipeline</button>
                <button onClick={() => switchMode("inbox")} className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 text-white/95 hover:text-white/90 transition-all"><Inbox className="w-4 h-4" />Inbox</button>
              </div>
              <PipelineDashboard pipeline={pipeline} onContactClick={handlePipelineContactClick} />
            </div>
          ) : mode === "inbox" ? (
            <div className="w-full max-w-4xl">
              <div className="flex bg-[#0a1520] border border-white/[0.08] rounded-xl p-1 gap-1 mb-8 max-w-lg">
                <button onClick={() => switchMode("signal")} className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 text-white/95 hover:text-white/90 transition-all"><Mail className="w-4 h-4" />Signals</button>
                <button onClick={() => switchMode("decide")} className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 text-white/95 hover:text-white/90 transition-all"><Compass className="w-4 h-4" />Decide</button>
                <button onClick={() => switchMode("pipeline")} className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 text-white/95 hover:text-white/90 transition-all"><BarChart3 className="w-4 h-4" />Pipeline</button>
                <button className="flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 bg-[#15283E] text-white border border-white/[0.1]"><Inbox className="w-4 h-4" />Inbox</button>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <Inbox className="w-5 h-5 text-[#C2440E]" />
                <h2 className="font-heading font-black text-xl text-white">AURIX Inbox</h2>
                <span className="font-mono text-[10px] text-white/95 tracking-wider">{inboxItems.length} items</span>
              </div>

              {/* Connection Status Card */}
              {!webhookStatus.connected ? (
                <div className="space-y-4 mb-6">
                  <div className="bg-[#15283E] border border-white/[0.08] rounded-xl p-6 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-[#25D366]/15 flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-7 h-7 text-[#25D366]" />
                    </div>
                    <p className="text-white text-lg font-bold mb-2">Connect WhatsApp</p>
                    <p className="text-white/85 text-sm mb-6 leading-relaxed max-w-md mx-auto">One-click setup. Connect your WhatsApp to auto-analyze conversations via AURIX Wingman.</p>
                    <button data-testid="webhook-connect-btn" onClick={async () => { await handleWebhookConnect(); setShowConnectPopup(true); }}
                      className="w-full max-w-xs mx-auto bg-[#25D366] hover:bg-[#1fba59] text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2.5 transition-all active:scale-[0.97] shadow-lg shadow-[#25D366]/20 text-lg">
                      <MessageSquare className="w-5 h-5" /> Connect WhatsApp
                    </button>
                    <p className="text-white/70 text-xs mt-4">Or use the <button onClick={() => switchMode("signal")} className="text-[#C2440E] underline underline-offset-2">Signals tab</button> to paste conversations directly.</p>
                  </div>
                </div>
              ) : (
                <div className="mb-6">
                  <div className="bg-[#15283E] border border-[#1D6B52]/30 rounded-xl p-5 flex items-center justify-between" style={{ boxShadow: "inset 0 0 20px rgba(29,107,82,0.06)" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#25D366]/15 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-[#25D366]" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#2dd4a0]" />
                          <span className="text-white font-semibold text-sm">WhatsApp Connected</span>
                        </div>
                        <p className="text-white/70 text-xs mt-0.5">Auto-analyzing incoming conversations</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid="show-setup-info" onClick={() => setShowConnectPopup(true)}
                        className="w-8 h-8 rounded-lg border border-white/[0.1] flex items-center justify-center text-white/70 hover:text-white hover:border-white/[0.2] transition-all" title="Setup instructions">
                        <HelpCircle className="w-4 h-4" />
                      </button>
                      <button data-testid="webhook-disconnect-btn" onClick={handleWebhookDisconnect}
                        className="text-xs text-white/70 hover:text-orange-400 transition-colors font-mono flex items-center gap-1.5 px-3 py-2 border border-white/[0.08] rounded-lg hover:border-orange-400/30">
                        Disconnect
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Connect WhatsApp Popup */}
              {showConnectPopup && webhookStatus.webhook_key && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70" onClick={() => setShowConnectPopup(false)}>
                  <div className="bg-[#15283E] border border-white/[0.1] rounded-2xl p-8 max-w-md w-full mx-4 relative" onClick={e => e.stopPropagation()}
                    style={{ boxShadow: "0 25px 50px rgba(0,0,0,0.5)" }}>
                    <button onClick={() => setShowConnectPopup(false)} className="absolute top-4 right-4 text-white/70 hover:text-white"><X className="w-5 h-5" /></button>

                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-xl bg-[#25D366]/15 flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-[#25D366]" />
                      </div>
                      <h3 className="text-white font-bold text-lg">Connect WhatsApp</h3>
                    </div>

                    {/* Step 1 */}
                    <div className="space-y-5">
                      <div className="flex gap-3">
                        <span className="font-mono text-sm font-bold text-[#25D366] w-6 shrink-0">1</span>
                        <div>
                          <p className="text-white font-semibold text-sm">Open WhatsApp</p>
                          <a href={`https://wa.me/14153783939?text=${encodeURIComponent(`Hey Wingman, I'm connecting DecideFast. My webhook URL is: ${BACKEND_URL}/api/webhook/incoming?key=${webhookStatus.webhook_key}\n\nPlease save this URL. From now on, whenever I share a conversation or transcript with you, POST it as JSON to this webhook URL with body: {"text": "the conversation", "source": "whatsapp"}. Then reply to me with the "reply" field from the response.`)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 mt-2 bg-[#25D366] hover:bg-[#1fba59] text-white font-semibold py-2.5 px-5 rounded-lg transition-all text-sm">
                            <ExternalLink className="w-4 h-4" /> Open WhatsApp
                          </a>
                        </div>
                      </div>

                      <div className="border-t border-white/[0.06] pt-4">
                        <div className="flex gap-3">
                          <span className="font-mono text-sm font-bold text-[#25D366] w-6 shrink-0">2</span>
                          <div>
                            <p className="text-white font-semibold text-sm">Send the pre-filled message</p>
                            <p className="text-white/70 text-xs mt-1 leading-relaxed">The message includes your personal webhook URL. AURIX will save it and use it to send conversation analysis to your DecideFast inbox.</p>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-white/[0.06] pt-4">
                        <div className="flex gap-3">
                          <span className="font-mono text-sm font-bold text-[#25D366] w-6 shrink-0">3</span>
                          <div>
                            <p className="text-white font-semibold text-sm">Start analyzing</p>
                            <p className="text-white/70 text-xs mt-1 leading-relaxed">Forward any WhatsApp conversation to AURIX. It will analyze it and the results appear here in your inbox automatically.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Webhook URL for manual copy */}
                    <div className="mt-6 bg-[#0a1520] rounded-lg p-3">
                      <p className="font-mono text-[9px] font-bold tracking-[0.2em] uppercase text-white/70 mb-1.5">Your Webhook URL</p>
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-[11px] text-[#2dd4a0] break-all select-all flex-1">{BACKEND_URL}/api/webhook/incoming?key={webhookStatus.webhook_key}</p>
                        <button onClick={() => { navigator.clipboard.writeText(`${BACKEND_URL}/api/webhook/incoming?key=${webhookStatus.webhook_key}`); }}
                          className="shrink-0 text-white/70 hover:text-[#2dd4a0] transition-colors p-1"><Copy className="w-4 h-4" /></button>
                      </div>
                    </div>

                    <p className="text-white/50 text-xs mt-4 text-center">Don't have AURIX? Set up a <a href="https://wa.me/14153783939" target="_blank" rel="noopener noreferrer" className="text-[#C2440E] underline underline-offset-2">WhatsApp Wingman</a> first.</p>
                  </div>
                </div>
              )}

              {/* Inbox Items */}
              {inboxItems.length === 0 ? (
                <div className="flex flex-col items-center gap-4 py-12 text-center">
                  <Inbox className="w-10 h-10 text-white/60" />
                  <p className="font-mono text-sm text-white/90 tracking-widest uppercase">{webhookStatus.connected ? "Waiting for AURIX data" : "Connect AURIX to get started"}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {inboxItems.map((item) => (
                    <div key={item.id} data-testid={`inbox-item-${item.id}`}
                      className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5 hover:-translate-y-0.5 transition-transform cursor-pointer"
                      onClick={() => { if (item.signal_id) { handleSelectHistory({ id: item.signal_id, type: "signal" }); } else { setInputText(item.text); setMode("signal"); } }}
                      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${item.status === "analyzed" ? "bg-[#2dd4a0]" : item.status === "analysis_failed" ? "bg-orange-400" : "bg-amber-400"}`} />
                          <span className="font-mono text-[10px] tracking-wider uppercase text-white/95">{item.source} | {item.status}</span>
                        </div>
                        <span className="font-mono text-[10px] text-white/90">{timeAgo(item.created_at)}</span>
                      </div>
                      <p className="text-white/95 text-sm leading-relaxed line-clamp-3">{item.text?.substring(0, 200)}...</p>
                      {item.contact_name && <p className="text-white/85 text-xs mt-2">Contact: {item.contact_name}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {!hasOutput && !loading && (
                <div className="w-full max-w-3xl animate-fade-in">
                  {/* Welcome header */}
                  <div className="mb-8 text-center">
                    <h1 className="font-heading font-black text-2xl md:text-3xl text-white tracking-tight mb-2">
                      {mode === "signal" ? "Capture Signals" : "Make a Decision"}
                    </h1>
                    <p className="text-white/70 text-sm max-w-md mx-auto leading-relaxed">
                      {mode === "signal"
                        ? "Paste an email, WhatsApp thread, or conversation — DecideFast extracts the real signals."
                        : "Have signals already? Great. Otherwise, start with Signals first for sharper decisions."}
                    </p>
                  </div>

                  {/* Signal-first nudge on Decide tab */}
                  {mode === "decide" && history.filter(h => h.type === "signal").length === 0 && (
                    <button data-testid="nudge-signals-first" onClick={() => switchMode("signal")}
                      className="w-full mb-6 px-5 py-4 bg-[#C2440E]/10 border border-[#C2440E]/25 rounded-xl flex items-center gap-4 hover:bg-[#C2440E]/15 transition-all group text-left">
                      <div className="w-10 h-10 rounded-xl bg-[#C2440E]/20 flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5 text-[#C2440E]" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-semibold text-sm">No signals captured yet</p>
                        <p className="text-white/60 text-xs mt-0.5">Capture signals first for better decisions. Tap here to switch to Signals.</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#C2440E] group-hover:translate-x-1 transition-transform" />
                    </button>
                  )}

                  {/* How it works — 3 step flow */}
                  <div className="grid grid-cols-3 gap-3 mb-8">
                    <div className={`rounded-xl p-4 border text-center ${mode === "signal" ? "bg-[#C2440E]/10 border-[#C2440E]/30" : "bg-white/[0.03] border-white/[0.06]"}`}>
                      <div className={`w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center ${mode === "signal" ? "bg-[#C2440E]/20" : "bg-white/[0.06]"}`}>
                        <Mail className={`w-4 h-4 ${mode === "signal" ? "text-[#C2440E]" : "text-white/50"}`} />
                      </div>
                      <p className={`font-mono text-[9px] font-bold tracking-[0.15em] uppercase ${mode === "signal" ? "text-[#C2440E]" : "text-white/50"}`}>1. Capture</p>
                      <p className="text-white/60 text-[11px] mt-1">Paste conversations</p>
                    </div>
                    <div className={`rounded-xl p-4 border text-center ${mode === "decide" ? "bg-[#1D6B52]/10 border-[#1D6B52]/30" : "bg-white/[0.03] border-white/[0.06]"}`}>
                      <div className={`w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center ${mode === "decide" ? "bg-[#1D6B52]/20" : "bg-white/[0.06]"}`}>
                        <Compass className={`w-4 h-4 ${mode === "decide" ? "text-[#2dd4a0]" : "text-white/50"}`} />
                      </div>
                      <p className={`font-mono text-[9px] font-bold tracking-[0.15em] uppercase ${mode === "decide" ? "text-[#2dd4a0]" : "text-white/50"}`}>2. Decide</p>
                      <p className="text-white/60 text-[11px] mt-1">Get clear direction</p>
                    </div>
                    <div className="rounded-xl p-4 border bg-white/[0.03] border-white/[0.06] text-center">
                      <div className="w-8 h-8 rounded-lg bg-white/[0.06] mx-auto mb-2 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-white/50" />
                      </div>
                      <p className="font-mono text-[9px] font-bold tracking-[0.15em] uppercase text-white/50">3. Track</p>
                      <p className="text-white/60 text-[11px] mt-1">Build your pipeline</p>
                    </div>
                  </div>

                  {/* Founder's Brief */}
                  <div data-testid="founders-brief" className="bg-[#15283E] border border-white/[0.08] rounded-xl p-5" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)" }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-md bg-[#6C5CE7]/15 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-[#6C5CE7]" />
                      </div>
                      <span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/60">Your Weekly Pulse</span>
                    </div>
                    {briefLoading ? (
                      <div className="flex items-center gap-2 py-2"><Loader2 className="w-4 h-4 text-white/40 animate-spin" /><span className="text-white/40 text-sm">Loading brief...</span></div>
                    ) : foundersBrief ? (
                      <div>
                        <p className="text-white/90 text-[14px] leading-relaxed">{foundersBrief.brief}</p>
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/[0.06]">
                          <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#C2440E]" /><span className="font-mono text-[10px] text-white/50">{foundersBrief.signal_count} signals</span></div>
                          <div className="flex items-center gap-1.5"><Compass className="w-3.5 h-3.5 text-[#2dd4a0]" /><span className="font-mono text-[10px] text-white/50">{foundersBrief.decision_count} decisions</span></div>
                          {foundersBrief.hot_deals > 0 && <div className="flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-orange-400" /><span className="font-mono text-[10px] text-white/50">{foundersBrief.hot_deals} hot</span></div>}
                        </div>
                      </div>
                    ) : (
                      <p className="text-white/50 text-sm">No signals captured yet — paste your first conversation to get your weekly pulse.</p>
                    )}
                  </div>

                  {/* Recent Conversations (tagged) */}
                  {(() => {
                    const taggedItems = history.filter(h => h.conversation_tag);
                    const uniqueTags = [];
                    const seen = new Set();
                    for (const item of taggedItems) {
                      if (!seen.has(item.conversation_tag)) {
                        seen.add(item.conversation_tag);
                        uniqueTags.push(item);
                      }
                    }
                    if (uniqueTags.length === 0) return null;
                    return (
                      <div data-testid="recent-conversations" className="mt-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-white/50">Continue a conversation</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {uniqueTags.slice(0, 8).map((item) => (
                            <button key={item.conversation_tag} data-testid={`tag-chip-${item.conversation_tag}`}
                              onClick={() => {
                                setInputText(item.conversation_tag + " ");
                                setConversationTag(item.conversation_tag);
                                const textarea = document.querySelector('[data-testid="prompt-input-textarea"]');
                                if (textarea) textarea.focus();
                              }}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-[#6C5CE7]/8 border border-[#6C5CE7]/20 rounded-lg hover:bg-[#6C5CE7]/15 hover:border-[#6C5CE7]/35 transition-all group text-left">
                              <span className="font-mono text-[11px] font-bold text-[#6C5CE7]">{item.conversation_tag}</span>
                              <span className="text-white/50 text-[11px] truncate max-w-[140px]">{item.type === "signal" ? (item.contact_name || item.input_preview) : (item.input_preview || item.recommendation)}</span>
                              <ChevronRight className="w-3 h-3 text-[#6C5CE7]/50 group-hover:translate-x-0.5 transition-transform shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Empty state — no history yet */}
                  {history.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-white/40 text-xs">
                        {mode === "signal" ? "Paste your first email or conversation on the left to get started" : "Describe your situation on the left to get a clear decision"}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {loading && (
                <div className="flex-1 flex flex-col items-center justify-center gap-5 text-center animate-fade-in" style={{ minHeight: "60vh" }}>
                  <div className="flex gap-2.5"><span className="w-3 h-3 rounded-full bg-[#C2440E] pulse-dot-1" /><span className="w-3 h-3 rounded-full bg-[#C2440E] pulse-dot-2" /><span className="w-3 h-3 rounded-full bg-[#C2440E] pulse-dot-3" /></div>
                  <p data-testid="loading-indicator" className="font-mono text-sm text-white/90 tracking-widest uppercase">Extracting signals & deciding</p>
                </div>
              )}
              {decision && !loading && signal && (
                <div className="w-full max-w-3xl space-y-4 animate-slide-up">
                  {/* Analyzed Sources */}
                  {analyzedSources.length > 0 && (
                    <div data-testid="analyzed-sources" className="flex flex-wrap items-center gap-2 px-1">
                      <span className="font-mono text-[9px] text-white/40 tracking-wider uppercase">Analyzed:</span>
                      {analyzedSources.map((s, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.04] border border-white/[0.08] rounded-md text-[11px] text-white/60">
                          {s.type === "link" ? <ExternalLink className="w-3 h-3" /> : s.type === "file" ? <FileText className="w-3 h-3" /> : <FileDown className="w-3 h-3" />}
                          <span className="truncate max-w-[180px]">{s.name}</span>
                          {s.type === "link" && !s.success && <span className="text-orange-400 text-[9px]">(failed)</span>}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Conversation Tag */}
                  {conversationTag && (
                    <div data-testid="conversation-tag" className="flex items-center gap-2 px-1">
                      <span className="font-mono text-[9px] text-white/40 tracking-wider uppercase">Conversation:</span>
                      <button onClick={() => { navigator.clipboard.writeText(conversationTag); }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#6C5CE7]/10 border border-[#6C5CE7]/25 rounded-md text-[12px] text-[#6C5CE7] font-mono font-bold hover:bg-[#6C5CE7]/20 transition-all"
                        title="Click to copy tag — use in new conversation to continue">
                        {conversationTag} <Copy className="w-3 h-3" />
                      </button>
                      <span className="text-[10px] text-white/30">Use this tag to continue this conversation</span>
                    </div>
                  )}
                  {/* Signal Summary Card (collapsed) */}
                  <div data-testid="auto-signal-summary" className="bg-[#15283E] border border-[#C2440E]/20 rounded-xl p-5" style={{ boxShadow: "inset 0 0 20px rgba(194,68,14,0.04)" }}>
                    <div className="flex items-center gap-2.5 mb-3">
                      <div className="w-7 h-7 rounded-md bg-[#C2440E]/15 flex items-center justify-center"><Mail className="w-4 h-4 text-[#C2440E]" /></div>
                      <span className="font-mono text-[10px] font-bold tracking-[0.2em] uppercase text-[#C2440E]">Signals Extracted</span>
                      <ScoreBadge score={signal.signal_score} />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <p className="font-mono text-[9px] text-white/50 uppercase tracking-wider mb-1">Contact</p>
                        <p className="text-white/90 text-sm font-medium">{signal.contact_name} ({signal.contact_type})</p>
                      </div>
                      <div>
                        <p className="font-mono text-[9px] text-white/50 uppercase tracking-wider mb-1">Stage</p>
                        <p className="text-white/90 text-sm">{signal.deal_stage}</p>
                      </div>
                    </div>
                    {signal.key_insights.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-white/[0.06]">
                        <p className="font-mono text-[9px] text-white/50 uppercase tracking-wider mb-2">Key Insights</p>
                        <div className="space-y-1.5">{signal.key_insights.map((ins, i) => (
                          <div key={i} className="flex gap-2 items-start"><div className="w-1 h-1 rounded-full bg-amber-400 mt-2 shrink-0" /><p className="text-white/70 text-[12px] leading-relaxed">{ins}</p></div>
                        ))}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 px-2"><div className="flex-1 h-px bg-white/[0.06]" /><span className="font-mono text-[9px] text-white/40 tracking-widest uppercase">Decision</span><div className="flex-1 h-px bg-white/[0.06]" /></div>
                </div>
              )}
              {decision && !loading && <DecisionCards decision={decision} onShare={handleShare} onReset={handleReset} copied={copied} onStepClick={handleStepClick} onCommit={handleCommitDecision} committedOption={committedOption} conversationTag={conversationTag} />}
              {signal && !decision && !loading && (
                <>
                  {conversationTag && (
                    <div className="w-full max-w-3xl mb-4">
                      <div data-testid="conversation-tag-standalone" className="flex items-center gap-2 px-1">
                        <span className="font-mono text-[9px] text-white/40 tracking-wider uppercase">Conversation:</span>
                        <button onClick={() => { navigator.clipboard.writeText(conversationTag); }}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#6C5CE7]/10 border border-[#6C5CE7]/25 rounded-md text-[12px] text-[#6C5CE7] font-mono font-bold hover:bg-[#6C5CE7]/20 transition-all"
                          title="Click to copy tag">
                          {conversationTag} <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}
                  <SignalCards signal={signal} onShare={handleShare} onReset={handleReset} copied={copied} onDecideAbout={handleDecideAboutSignal} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile output overlay */}
      <div className="md:hidden">
        {(hasOutput || loading) && mode !== "pipeline" && mode !== "inbox" && (
          <div className="fixed inset-0 z-50 bg-[#0D1B2A] overflow-y-auto custom-scrollbar p-5 pt-16">
            <button data-testid="close-mobile-output" onClick={() => { if (!loading) handleReset(); }} className="fixed top-4 right-4 z-50 w-10 h-10 rounded-lg bg-[#15283E] border border-white/[0.15] flex items-center justify-center text-white"><X className="w-4 h-4" /></button>
            {loading && (<div className="flex flex-col items-center justify-center h-64 gap-5"><div className="flex gap-2.5"><span className="w-3 h-3 rounded-full bg-[#C2440E] pulse-dot-1" /><span className="w-3 h-3 rounded-full bg-[#C2440E] pulse-dot-2" /><span className="w-3 h-3 rounded-full bg-[#C2440E] pulse-dot-3" /></div><p className="font-mono text-sm text-white/90 tracking-widest uppercase">Analyzing</p></div>)}
            {decision && !loading && <DecisionCards decision={decision} onShare={handleShare} onReset={handleReset} copied={copied} onStepClick={handleStepClick} onCommit={handleCommitDecision} committedOption={committedOption} />}
            {signal && !decision && !loading && <SignalCards signal={signal} onShare={handleShare} onReset={handleReset} copied={copied} onDecideAbout={handleDecideAboutSignal} />}
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  );
}

function AuthGate() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-[#C2440E] animate-spin" />
          <p className="font-mono text-sm text-white/95 tracking-widest uppercase">Loading</p>
        </div>
      </div>
    );
  }

  if (!user) return <LoginPage />;
  return <DecideFastApp />;
}

export default App;
